import { quoteRequestApi } from 'apiConnector/quote/quote';
import * as apiErrorHelper from 'epic/apiErrorEpicHelper';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics/tabRemoved';
import { ofType } from 'redux-observable';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import {
  dynamicQuoteAckedThunk,
  dynamicQuoteRequestFailedThunk,
  dynamicStreamingQuoteAckedThunk,
} from 'state/dynamic/dynamicForm.thunks';
import { QuoteRequested } from 'state/forms/actions/quote';
import { commonStreamActions } from 'state/forms/actions/stream';
import { getFormRequestType } from 'state/selectors';
import type { EpicWithThunk } from 'state/state.common';
import { now } from 'utils/core/now';

export function requestQuoteEpic(): EpicWithThunk {
  return (
    action$,
    state$,
    { httpClient, frontLogger, selectors: { getDynamicFormInfos, getFormEnrichedData }, getSgmeConfiguration },
  ) =>
    action$.pipe(
      ofType('QUOTE_REQUESTED', 'STREAM_REQUESTED'),
      filter(a => a.workspace === 'dynamic'),
      withLatestFrom(state$),
      mergeMap(([{ tabId, quoteType, quoteRequestStartingTimeInMillis, isReprice, ...action }, state]) => {
        const { quoteQuery, negotiationId, instrumentName, instrumentContractName, version, sendWholeFormData } =
          getDynamicFormInfos(state, tabId)!;
        const frontReconciliationId = (action as QuoteRequested).frontReconciliationId;
        const rfq = sendWholeFormData ? getFormEnrichedData(state, tabId) : quoteQuery;

        const quoteAckTimeoutMs = getSgmeConfiguration().quoteAckTimeoutMs;

        const tabRemoved$ = tabRemoved(action$, tabId, state$);
        const requestType = getFormRequestType(state, 'dynamic', tabId);

        return quoteRequestApi(httpClient, quoteAckTimeoutMs)
          .requestQuote(
            {
              quoteQuery: rfq,
              negotiationId,
              instrumentName,
              instrumentContractName,
              version,
              tabId,
              reconciliationId: frontReconciliationId,
              isReprice,
            },
            'dynamic',
            quoteType,
          )
          .pipe(
            takeUntil(tabRemoved$),
            map(
              ({
                quoteId,
                quoteExternalId,
                pricingEstimatedDurationInMilliseconds,
                newVersion,
                isExecOnlyAddon,
                details,
                sidePanelDetails,
              }) => {
                if (requestType === 'RequestForStream') {
                  return dynamicStreamingQuoteAckedThunk(
                    tabId,
                    quoteId,
                    quoteType,
                    quoteExternalId,
                    negotiationId,
                    pricingEstimatedDurationInMilliseconds,
                    quoteRequestStartingTimeInMillis,
                    newVersion,
                    isExecOnlyAddon,
                  );
                } else {
                  return dynamicQuoteAckedThunk(
                    tabId,
                    quoteId,
                    quoteType,
                    quoteExternalId,
                    negotiationId,
                    pricingEstimatedDurationInMilliseconds,
                    quoteRequestStartingTimeInMillis,
                    newVersion,
                    isExecOnlyAddon,
                    details,
                    frontReconciliationId,
                    sidePanelDetails,
                  );
                }
              },
            ),
            catchError((error: Error) => {
              frontLogger.error('An error occurred during dynamic quote request {@FrontError}', error);
              const quoteRequestFailed = dynamicQuoteRequestFailedThunk(
                tabId,
                apiErrorHelper.toApiErrorMessage(error, { timeoutErrorMessage: 'quote.errorMessages.timeout' }),
                frontReconciliationId,
              );
              const streamingFailed = commonStreamActions.streamingQuoteFailed(
                'dynamic',
                tabId,
                apiErrorHelper.getErrorRejections(error),
                now(),
              );

              if (requestType === 'RequestForStream') {
                return of(quoteRequestFailed, streamingFailed);
              }
              return of(quoteRequestFailed);
            }),
          );
      }),
    );
}
